import { DOCUMENT, isPlatformBrowser } from '@angular/common';
import { Injectable, PLATFORM_ID, inject, signal, computed } from '@angular/core';


const BRAND_KEY = 'theme.brand';
const MODE_KEY = 'theme.mode';
type ThemeBrand = 'softwaholic' | 'brand-a' | 'brand-b';
type ThemeMode = 'light' | 'dark' | 'system'; // system resolves to light/dark

@Injectable({ providedIn: 'root' })
export class ThemeService {
  private readonly doc = inject(DOCUMENT);
  private readonly platformId = inject(PLATFORM_ID);
  private readonly isBrowser = isPlatformBrowser(this.platformId);

  // reactive state
  readonly brand = signal<ThemeBrand>('softwaholic');
  readonly mode = signal<ThemeMode>('light');

  // resolved mode (system-aware)
  readonly resolvedMode = computed<'light' | 'dark'>(() => {
    const m = this.mode();
    if (m !== 'system') return m;
    return this.prefersDark() ? 'dark' : 'light';
  });

  init() {
    if (!this.isBrowser) return;
    // restore
    const savedBrand = (localStorage.getItem(BRAND_KEY) as ThemeBrand) || 'softwaholic';
    const savedMode = (localStorage.getItem(MODE_KEY) as ThemeMode) || 'light';
    this.brand.set(savedBrand);
    this.mode.set(savedMode);

    // apply and listen to system changes if needed
    this.apply();
    if (savedMode === 'system') {
      this.bindSystemListener();
    }
  }

  setBrand(brand: ThemeBrand) {
    this.brand.set(brand);
    if (this.isBrowser) localStorage.setItem(BRAND_KEY, brand);
    this.apply();
  }

  setMode(mode: ThemeMode) {
    this.mode.set(mode);
    if (this.isBrowser) localStorage.setItem(MODE_KEY, mode);
    this.apply();
    this.unbindSystemListener();
    if (mode === 'system') this.bindSystemListener();
  }

  toggleMode() {
    const cur = this.mode();
    if (cur === 'system') {
      // toggle between resolved modes
      this.setMode(this.prefersDark() ? 'light' : 'dark');
      return;
    }
    this.setMode(cur === 'light' ? 'dark' : 'light');
  }

  private apply() {
    const root = this.doc.documentElement;
    root.setAttribute('data-brand', this.brand());
    root.setAttribute('data-mode', this.resolvedMode());
  }

  private prefersDark(): boolean {
    if (!this.isBrowser || !window.matchMedia) return false;
    return window.matchMedia('(prefers-color-scheme: dark)').matches;
  }

  private mediaQuery?: MediaQueryList;
  private onMediaChange = (e: MediaQueryListEvent) => {
    if (this.mode() === 'system') this.apply();
  };

  private bindSystemListener() {
    if (!this.isBrowser || !window.matchMedia) return;
    this.mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    this.mediaQuery.addEventListener?.('change', this.onMediaChange);
  }

  private unbindSystemListener() {
    this.mediaQuery?.removeEventListener?.('change', this.onMediaChange);
    this.mediaQuery = undefined;
  }
}
